import { z } from 'zod';
export const LICENCE_PROTOCOL = 1;
export const LICENCE_SIGNING_DOMAIN = 'his-licence-v1\n';
export const LICENCE_SUPPORT_ACCESS_LIMIT = 20;
export const LICENCE_SUPPORT_ACCESS_DAYS = 90;
export const LICENCE_VERSION_PATTERN = /^\d{1,19}$/;
export const LICENCE_ENVIRONMENTS = ['live', 'demo'];
export const LICENCE_MODULE_STATES = ['enabled', 'retired'];
export const LICENCE_JSON_MAX_LENGTH = 64_000;
const licenceVersionSchema = z.string().regex(LICENCE_VERSION_PATTERN);
const isoDateTime = z.string().datetime();
const optionalIso = isoDateTime.nullable();
export const licenceModuleSchema = z.object({
    code: z.string().min(1).max(60),
    state: z.enum(LICENCE_MODULE_STATES),
});
export const licenceSupportAccessSchema = z.object({
    id: z.string().min(1).max(80),
    kind: z.literal('owner'),
    by: z.string().max(160),
    mode: z.literal('read'),
    reason: z.string().max(400),
    startedAt: isoDateTime,
    endedAt: optionalIso,
});
export const licenceDocumentSchema = z.object({
    v: z.literal(1),
    product: z.string().min(1).max(40),
    keyId: z.string().max(64),
    tenantId: z.string().min(1).max(80),
    licenceVersion: licenceVersionSchema,
    issuedAt: isoDateTime,
    installFingerprint: z.string().max(128).nullable(),
    facility: z.object({
        name: z.string().max(200),
        facilityType: z.string().max(40),
        environment: z.enum(LICENCE_ENVIRONMENTS),
        limits: z.record(z.unknown()),
        directoryEnabled: z.boolean(),
        shareHistoryWithinGroup: z.boolean(),
        shareHistoryAcrossPlatform: z.boolean(),
    }),
    packages: z.array(z.string().max(80)).max(20),
    modules: z.array(licenceModuleSchema).max(200),
    addOns: z.array(z.string().max(60)).max(60),
    lifecycle: z.object({
        status: z.string().max(40),
        readOnly: z.boolean(),
        blocked: z.boolean(),
        state: z.string().max(40),
        since: optionalIso,
        until: optionalIso,
        reasonCategory: z.string().max(40).nullable(),
        closureEndsAt: optionalIso,
        closureAccess: z.enum(['everyone', 'admins']).nullable(),
        deletionDueAt: optionalIso,
        trialEndsAt: optionalIso,
        paidUntil: optionalIso,
    }),
    offline: z.object({
        graceDays: z.number().int().min(0).max(3650),
        graceExtensionUntil: optionalIso,
        updateRequired: z.boolean(),
        requiredBy: optionalIso,
    }),
    supportAccess: z.array(licenceSupportAccessSchema).max(LICENCE_SUPPORT_ACCESS_LIMIT),
});
export const signedLicenceSchema = z.object({
    json: z.string().min(2).max(LICENCE_JSON_MAX_LENGTH),
    signature: z.string().max(200),
});
export const LICENCE_REJECTION_REASONS = [
    'MALFORMED',
    'NOT_CANONICAL',
    'WRONG_PRODUCT',
    'UNKNOWN_KEY',
    'BAD_SIGNATURE',
    'UNSIGNED',
    'NO_BAKED_KEY',
    'WRONG_TENANT',
    'WRONG_FINGERPRINT',
    'NOT_NEWER',
];
function canonicalValue(value) {
    if (value === null)
        return null;
    if (typeof value === 'bigint')
        return value.toString(10);
    if (value instanceof Date)
        return value.toISOString();
    if (Array.isArray(value))
        return value.map((entry) => (entry === undefined ? null : canonicalValue(entry)));
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error('canonical JSON cannot hold a non-finite number');
        return value;
    }
    if (typeof value === 'object') {
        const sorted = {};
        for (const key of Object.keys(value).sort()) {
            const entry = value[key];
            if (entry === undefined || typeof entry === 'function')
                continue;
            sorted[key] = canonicalValue(entry);
        }
        return sorted;
    }
    return value;
}
export function canonicalJson(value) {
    return JSON.stringify(canonicalValue(value));
}
export function canonicalLicenceJson(document) {
    return canonicalJson(document);
}
export function licenceSigningPayload(canonical) {
    return `${LICENCE_SIGNING_DOMAIN}${canonical}`;
}
export function parseLicenceJson(json) {
    let raw;
    try {
        raw = JSON.parse(json);
    }
    catch {
        return { ok: false, reason: 'MALFORMED' };
    }
    const parsed = licenceDocumentSchema.safeParse(raw);
    if (!parsed.success)
        return { ok: false, reason: 'MALFORMED' };
    if (canonicalJson(raw) !== json)
        return { ok: false, reason: 'NOT_CANONICAL' };
    return { ok: true, document: parsed.data };
}
export function compareLicenceVersions(a, b) {
    const left = typeof a === 'bigint' ? a : BigInt(a);
    const right = typeof b === 'bigint' ? b : BigInt(b);
    return left === right ? 0 : left > right ? 1 : -1;
}
export function isLicenceVersion(value) {
    return typeof value === 'string' && LICENCE_VERSION_PATTERN.test(value);
}
export const LICENCE_DELIVERY_STATES = ['applied', 'waiting', 'rejected', 'legacy'];
export function licenceDeliveryState(input) {
    if (!input.protocol)
        return 'legacy';
    if (input.version !== null && input.appliedVersion === input.version)
        return 'applied';
    if (input.version !== null && input.rejectedReason?.startsWith(`${input.version}:`))
        return 'rejected';
    return 'waiting';
}
//# sourceMappingURL=licence.js.map